import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { io } from 'socket.io-client';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../Layout/DashboardLayout';
import LoadingSpinner from '../Common/LoadingSpinner';
import EmojiPicker from 'emoji-picker-react';
import { requestHelpers } from '../../services/api';
import './Chat.css';

const Chat = () => {
    const [conversations, setConversations] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [mobilePane, setMobilePane] = useState('list'); // 'list' or 'chat'
    const [newMessage, setNewMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [isStartChatModalOpen, setIsStartChatModalOpen] = useState(false);
    const [newChatNumber, setNewChatNumber] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const { user } = useAuth();
    const socketRef = useRef();
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const selectedChatRef = useRef(selectedChat);

    useEffect(() => {
        selectedChatRef.current = selectedChat;
    }, [selectedChat]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        // Initialize Socket
        let socketUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

        try {
            // Use the absolute URL if provided, or resolve relative to current origin
            const parsedUrl = new URL(socketUrl, window.location.origin);
            socketUrl = parsedUrl.origin;
        } catch (err) {
            console.error('❌ Failed to parse socket URL, falling back to localhost:', err);
            socketUrl = 'http://localhost:5000';
        }

        console.log('🔌 Attempting Socket Connection to:', socketUrl);

        const socket = io(socketUrl, {
            withCredentials: true,
            transports: ['polling', 'websocket'],
            reconnectionAttempts: 5,
            timeout: 10000
        });
        socketRef.current = socket;

        socket.on('connect', () => {
            const joinId = (user?._id || user?.id)?.toString();
            console.log('✅ Connected to socket server. Socket ID:', socket.id);
            console.log('📡 User context for room:', joinId);
            
            if (joinId) {
                console.log('🚀 Emitting join to room:', joinId);
                socket.emit('join', joinId);
            } else {
                console.warn('⚠️ No User ID found in context. Socket updates will not be routed.');
            }
        });

        socket.on('connect_error', (error) => {
            console.error('❌ Socket Connection Error:', error);
        });

        socket.on('reconnect_attempt', (attempt) => {
            console.log('🔄 Socket Reconnection Attempt:', attempt);
        });

        socket.on('newMessage', (message) => {
            console.log('📥 Socket Message Received:', message);
            
            const incomingConvId = message.conversationId?.toString();
            const selectedConvId = selectedChatRef.current?._id?.toString();
            
            // 1. Update Messages view if chat is selected
            if (selectedConvId && incomingConvId === selectedConvId) {
                setMessages(prev => {
                    const isDuplicate = prev.find(m => 
                        m._id?.toString() === message._id?.toString() || 
                        (m.whatsappMessageId && m.whatsappMessageId === message.whatsappMessageId)
                    );
                    if (isDuplicate) return prev;
                    return [...prev, message];
                });
            }

            // 2. Optimistic Sidebar Update (Faster than fetchConversations)
            setConversations(prev => {
                const existing = prev.find(c => c._id?.toString() === incomingConvId);
                if (existing) {
                    const updated = prev.map(c => {
                        if (c._id?.toString() === incomingConvId) {
                            return {
                                ...c,
                                lastMessage: {
                                    text: message.text || `[${message.type}]`,
                                    sender: message.sender,
                                    timestamp: message.timestamp || new Date()
                                },
                                unreadCount: selectedConvId === incomingConvId ? 0 : (c.unreadCount || 0) + (message.sender === 'customer' ? 1 : 0)
                            };
                        }
                        return c;
                    });
                    // Move the updated conversation to top
                    return [...updated].sort((a, b) => 
                        new Date(b.lastMessage?.timestamp || 0) - new Date(a.lastMessage?.timestamp || 0)
                    );
                } else {
                    // Fallback to fetch if it's a completely new conversation we don't have
                    fetchConversations();
                    return prev;
                }
            });
        });

        socket.on('statusUpdate', (update) => {
            console.log('🔄 Status Update Received:', update);
            
            // 1. Update messages in current view
            setMessages(prev => prev.map(msg =>
                msg._id === update.messageId || (msg.whatsappMessageId && msg.whatsappMessageId === update.whatsappMessageId)
                    ? { ...msg, status: update.status }
                    : msg
            ));

            // 2. Sync Unread Status in sidebar (Optimistic)
            if (update.status === 'read' && update.conversationId) {
                setConversations(prev => prev.map(conv => 
                    conv._id === update.conversationId 
                        ? { ...conv, unreadCount: 0 } 
                        : conv
                ));
            }
        });

        fetchConversations();

        return () => {
            socket.disconnect();
        };
    }, [user]); // Only re-run when user changes

    useEffect(() => {
        if (selectedChat) {
            fetchMessages(selectedChat._id);
        }
    }, [selectedChat]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const fetchConversations = async () => {
        try {
            const response = await requestHelpers.get('/chat/conversations');
            setConversations(response.data || []);
            if (response.data?.length > 0 && !selectedChat) {
                setSelectedChat(response.data[0]);
            }
        } catch (error) {
            console.error('Failed to fetch conversations:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchMessages = async (chatId) => {
        try {
            const response = await requestHelpers.get(`/chat/messages/${chatId}`);
            setMessages(response.data || []);
        } catch (error) {
            console.error('Failed to fetch messages:', error);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !selectedChat) return;

        const tempMessage = newMessage;
        setNewMessage('');

        try {
            await requestHelpers.post('/chat/send', {
                conversationId: selectedChat._id,
                text: tempMessage
            });
            fetchConversations();
        } catch (error) {
            toast.error('Error sending message');
        }
    };

    const handleStartChat = async (e) => {
        e.preventDefault();
        if (!newChatNumber.trim()) return;

        setIsVerifying(true);
        try {
            const response = await requestHelpers.post('/chat/start-chat', { phoneNumber: newChatNumber });
            toast.success('Number verified! Starting chat...');
            setIsStartChatModalOpen(false);
            setNewChatNumber('');
            await fetchConversations();
        } catch (error) {
            toast.error(error.message || 'Error verifying number');
        } finally {
            setIsVerifying(false);
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file || !selectedChat) return;

        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('purpose', 'chat');

        try {
            // 1. Upload to Media Service
            const uploadRes = await requestHelpers.post('/media/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (uploadRes.data?.success) {
                const { mediaId, fileType } = uploadRes.data.data;
                
                // 2. Send via Chat API
                await requestHelpers.post('/chat/send', {
                    conversationId: selectedChat._id,
                    type: fileType,
                    mediaUrl: uploadRes.data.data.url, // Fallback url if server logic needs it
                    id: mediaId,
                    text: `Sent a ${fileType}`
                });
                
                toast.success(`${fileType.charAt(0).toUpperCase() + fileType.slice(1)} sent!`);
                fetchConversations();
            }
        } catch (error) {
            console.error('File upload error:', error);
            toast.error(error.response?.data?.message || 'Error sending file');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const onEmojiClick = (emojiObject) => {
        setNewMessage(prev => prev + emojiObject.emoji);
    };

    const formatDateSeparator = (date) => {
        const now = new Date();
        const msgDate = new Date(date);
        
        if (msgDate.toDateString() === now.toDateString()) return 'Today';
        
        const yesterday = new Date();
        yesterday.setDate(now.getDate() - 1);
        if (msgDate.toDateString() === yesterday.toDateString()) return 'Yesterday';
        
        return msgDate.toLocaleDateString(undefined, { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    };

    const renderMessages = () => {
        const elements = [];
        let lastDate = null;

        messages.forEach((msg, index) => {
            const msgDate = new Date(msg.timestamp).toDateString();
            
            if (msgDate !== lastDate) {
                elements.push(
                    <div key={`date-${msgDate}`} className="date-separator">
                        <span>{formatDateSeparator(msg.timestamp)}</span>
                    </div>
                );
                lastDate = msgDate;
            }

            elements.push(
                <div key={msg._id || index} className={`message-wrapper ${msg.sender === 'me' ? 'me' : 'them'}`}>
                    <div className="message-content">
                        {msg.type === 'text' || !msg.type ? (
                            <p>{msg.text}</p>
                        ) : (
                            <div className="media-message">
                                {msg.type === 'image' && <img src={msg.mediaUrl} alt="sent image" className="chat-image" />}
                                {msg.type === 'video' && <video src={msg.mediaUrl} controls className="chat-video" />}
                                {msg.type === 'audio' && <audio src={msg.mediaUrl} controls className="chat-audio" />}
                                {msg.type === 'document' && (
                                    <a href={msg.mediaUrl} target="_blank" rel="noopener noreferrer" className="document-link">
                                        <i className="fas fa-file-alt"></i>
                                        <span>Download Document</span>
                                    </a>
                                )}
                                <p className="media-caption">{msg.text !== `Sent a ${msg.type}` ? msg.text : ''}</p>
                            </div>
                        )}
                        <div className="message-meta">
                            <span className="message-time">{formatTime(msg.timestamp)}</span>
                            {msg.sender === 'me' && (
                                <span className="message-status">
                                    {getStatusIcon(msg.status)}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            );
        });

        return elements;
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'sent': return <i className="fas fa-check"></i>;
            case 'delivered': return <i className="fas fa-check-double"></i>;
            case 'read': return <i className="fas fa-check-double read"></i>;
            default: return null;
        }
    };

    // ── Initials Avatar ──────────────────────
    const AVATAR_COLORS = [
        '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981',
        '#3b82f6', '#ef4444', '#14b8a6', '#f97316', '#06b6d4'
    ];
    const getAvatarColor = (name = '') => {
        let hash = 0;
        for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
        return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
    };
    const getInitials = (name = '') => {
        const parts = name.trim().split(/\s+/);
        if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
        return (name.slice(0, 2)).toUpperCase() || '?';
    };
    const InitialsAvatar = React.memo(({ name, size = 42 }) => (
        <div style={{
            width: size, height: size, minWidth: size,
            background: `linear-gradient(135deg, ${getAvatarColor(name)}, ${getAvatarColor(name + '1')})`,
            borderRadius: '50%', display: 'flex', alignItems: 'center',
            justifyContent: 'center', color: '#fff', fontWeight: 700,
            fontSize: size * 0.36, letterSpacing: '0.5px',
            userSelect: 'none', flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.18)'
        }}>
            {getInitials(name)}
        </div>
    ));
    // ──────────────────────────────────────
    const filteredConversations = React.useMemo(() => {
        return conversations.filter(conv =>
            conv.customerName.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [conversations, searchTerm]);


    if (loading) {
        return (
            <DashboardLayout>
                <div className="loading-container">
                    <LoadingSpinner />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className={`chat-container ${mobilePane === 'chat' ? 'show-chat' : 'show-list'}`}>
                <div className="chat-sidebar">
                    <div className="sidebar-header">
                        <div className="header-top">
                            <div>
                                <h2>Chats</h2>
                                {user?.whatsappNumber && (
                                    <div className="header-number-badge mt-1" style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', padding: '2px 10px', borderRadius: '20px', fontSize: '10px', fontWeight: 'bold' }}>
                                        <i className="fab fa-whatsapp"></i>
                                        <span>Linked to: {user.whatsappNumber}</span>
                                    </div>
                                )}
                            </div>
                            <button 
                                className="start-chat-btn" 
                                title="Start New Chat"
                                onClick={() => setIsStartChatModalOpen(true)}
                            >
                                <i className="fas fa-plus"></i>
                            </button>
                        </div>
                        <div className="search-bar">
                            <i className="fas fa-search"></i>
                            <input
                                type="text"
                                placeholder="Search chats..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="conversation-list">
                        {filteredConversations.map(conv => (
                            <div
                                key={conv._id}
                                className={`conversation-item ${selectedChat?._id === conv._id ? 'active' : ''} ${conv.unreadCount > 0 ? 'unread' : ''}`}
                                onClick={() => {
                                    setSelectedChat(conv);
                                    setMobilePane('chat');
                                }}
                            >
                                <div className="avatar-wrapper">
                                    <InitialsAvatar name={conv.customerName} size={46} />
                                    <span className={`status-indicator ${conv.status || 'offline'}`}></span>
                                </div>
                                <div className="conv-info">
                                    <div className="conv-header">
                                        <span className="conv-name">{conv.customerName}</span>
                                        <span className="conv-time">{formatTime(conv.lastMessage?.timestamp)}</span>
                                    </div>
                                    <div className="conv-footer">
                                        <p className="last-message">
                                            {conv.lastMessage?.sender === 'me' && <span className="me-prefix">You: </span>}
                                            {conv.lastMessage?.text}
                                        </p>
                                        {conv.unreadCount > 0 && <span className="unread-badge">{conv.unreadCount}</span>}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

            <div className="chat-main">
                    {selectedChat ? (
                        <>
                            <div className="chat-header">
                                <button className="mobile-back-btn" onClick={() => setMobilePane('list')}>
                                    <i className="fas fa-arrow-left"></i>
                                </button>
                                <div className="header-user">
                                    <div className="avatar-wrapper">
                                        <InitialsAvatar name={selectedChat.customerName} size={42} />
                                        <span className={`status-indicator ${selectedChat.status || 'offline'}`}></span>
                                    </div>
                                    <div className="user-details">
                                        <h3>{selectedChat.customerName}</h3>
                                        <p>{selectedChat.customerPhone}</p>
                                    </div>
                                    {/* {user?.whatsappNumber && (
                                        <div className="chat-sender-info">
                                            <small>Active Line: {user.whatsappNumber}</small>
                                        </div>
                                    )} */}
                                </div>
                                <div className="header-actions">
                                    {/* Removed non-functional buttons */}
                                </div>
                            </div>

                            <div className="messages-container">
                                {renderMessages()}
                                <div ref={messagesEndRef} />
                            </div>

                            <form className="message-input-area" onSubmit={handleSendMessage}>
                                <div className="emoji-picker-wrapper">
                                    <button
                                        type="button"
                                        className={`icon-btn ${showEmojiPicker ? 'active' : ''}`}
                                        title="Emojis"
                                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                    >
                                        <i className="far fa-smile"></i>
                                    </button>
                                    {showEmojiPicker && (
                                        <div className="emoji-picker-container">
                                            <EmojiPicker
                                                onEmojiClick={onEmojiClick}
                                                autoFocusSearch={false}
                                                theme="light"
                                                width={300}
                                                height={400}
                                            />
                                        </div>
                                    )}
                                </div>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    onChange={handleFileChange}
                                />
                                <button 
                                    type="button" 
                                    className={`icon-btn ${isUploading ? 'loading' : ''}`} 
                                    title="Attach File"
                                    onClick={() => fileInputRef.current.click()}
                                    disabled={isUploading}
                                >
                                    {isUploading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-paperclip"></i>}
                                </button>
                                <input
                                    type="text"
                                    placeholder="Type a message..."
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    onFocus={() => setShowEmojiPicker(false)}
                                    disabled={isUploading}
                                />
                                <button type="submit" className="send-btn" disabled={!newMessage.trim() || isUploading}>
                                    <i className="fas fa-paper-plane"></i>
                                </button>
                            </form>
                        </>
                    ) : (
                        <div className="no-chat-selected">
                            <i className="fas fa-comments"></i>
                            <h3>Select a conversation</h3>
                            <p>Choose a contact from the list to start chatting</p>
                        </div>
                    )}
                </div>

                {isStartChatModalOpen && (
                    <div className="modal-overlay" onClick={() => setIsStartChatModalOpen(false)}>
                        <div className="start-chat-modal" onClick={e => e.stopPropagation()}>
                            <div className="modal-header">
                                <div className="header-title-group">
                                    <div className="modal-icon">
                                        <i className="fas fa-comment-medical"></i>
                                    </div>
                                    <div className="text-group">
                                        <h3>Start New Chat</h3>
                                        <p>Connect with a new contact</p>
                                    </div>
                                </div>
                                <button className="modal-close-btn" onClick={() => setIsStartChatModalOpen(false)}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            <form onSubmit={handleStartChat}>
                                <div className="modal-body">
                                    <div className="form-group field-group-beside">
                                        <label className="modal-label">WhatsApp Number</label>
                                        <div className="input-with-icon-beside">
                                            <div className="icon-side">
                                                <i className="fab fa-whatsapp"></i>
                                            </div>
                                            <input
                                                type="text"
                                                className="modal-input"
                                                placeholder="+91 99999 99999"
                                                value={newChatNumber}
                                                onChange={(e) => setNewChatNumber(e.target.value)}
                                                autoFocus
                                                required
                                            />
                                        </div>
                                        <p className="input-help">Please include country code for best results</p>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setIsStartChatModalOpen(false)}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary" disabled={isVerifying || !newChatNumber.trim()}>
                                        {isVerifying ? (
                                            <>
                                                <i className="fas fa-circle-notch fa-spin"></i> Verifying...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-paper-plane"></i> Start Chat
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default Chat;
